var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Frustum, Object3D, Sphere, Vector3 } from "three";
import { initExtras, isTileset, propagateRefinementMode, resolveBoundingSpheres, resolveTransforms, } from "../../../data/formats/3d_tiles";
import { DebugDraw } from "../../../renderer/debug";
import { MathUtils } from "../../../math";
import { PreparingObject } from "../../preparing_object";
import { FileLoader } from "../../../data/loaders";
import { Batch3DModel, Composite, Instanced3DModel, PointCloud } from "..";
import { EvictionQueue } from "../../../utils/EvictionQueue";
import { GLTFModel } from "../GLTFModel";
export class Tileset3D extends PreparingObject {
    constructor(options) {
        var _a, _b;
        super();
        this.tileCost = 0;
        this.pickingManager = options.pickingManager;
        this.requestManager = options.requestManager;
        this.matrixAutoUpdate = false;
        this.frustumCulled = false;
        if (options.parent !== undefined) {
            // If the Tileset3D is nested inside another then there is some shared objects we must grab from the root
            // node.
            const rootTileObject = options.parent.extras.owner.extras.owner;
            this.materialRegistry = rootTileObject.materialRegistry;
            this.sharedSettings = rootTileObject.sharedSettings;
            this.tileCache = rootTileObject.tileCache;
            this.isRoot = false;
        }
        else {
            // If the Tileset3D is not nested inside another then we need to create new objects for shared state ready
            // to be shared with any potential nested tilesets.
            this.materialRegistry = {
                styleCallbacks: new Set(),
                clippingPlaneCallbacks: new Set(),
                invalidateCallbacks: new Set(),
                clippingPlanes: [],
            };
            this.sharedSettings = {
                styleDesc: Object.assign({}, options.styleDesc),
                maximumScreenSpaceError: (_a = options.maximumScreenSpaceError) !== null && _a !== void 0 ? _a : 16,
                liveTileCost: 0,
                tileBudget: (_b = options.tileBudget) !== null && _b !== void 0 ? _b : 1024,
                attributions: [],
            };
            this.tileCache = new EvictionQueue();
            this.isRoot = true;
        }
        // Initialize the extras field on the tilesetWithMeta and refine the type so we can inject our metadata
        const tilesetWithMeta = initExtras(options.tileset, this);
        // Resolve all refinement modes so we don't have to hold the state in our own tree walk.
        propagateRefinementMode(tilesetWithMeta.root, options.parent);
        // Resolve all tile transforms to get a full local -> world matrix for all tiles
        resolveTransforms(tilesetWithMeta.root, options.parent);
        // Resolve all bounding volumes to spheres.
        resolveBoundingSpheres(tilesetWithMeta.root);
        // Cache reference required metadata
        this.tilesetSource = options.tileset.extras.source;
        this.tileset = tilesetWithMeta;
    }
    get attributions() {
        return this.sharedSettings.attributions.concat(this.tilesetSource.attributions);
    }
    get maximumScreenSpaceError() {
        return this.sharedSettings.maximumScreenSpaceError;
    }
    set maximumScreenSpaceError(v) {
        this.sharedSettings.maximumScreenSpaceError = v;
    }
    get tileBudget() {
        return this.sharedSettings.tileBudget;
    }
    set tileBudget(v) {
        this.sharedSettings.tileBudget = v;
    }
    /**
     * This function needs to be called once per-frame before rendering is performed. This handles converting the
     * tileset node tree into a render list for ThreeJS to handle.
     */
    prepare(renderer, camera, frameData) {
        var _a;
        // Clear shared attributions
        this.sharedSettings.attributions.length = 0;
        const screenSize = renderer.getSize();
        // Clear children
        this.clear();
        // Mark a new frame for the tile cache, making all nodes potentially evict-able.
        //
        // We only do this if we're the root tileset. Children only need to touch tiles
        if (this.isRoot) {
            this.tileCache.newFrame();
        }
        // Perform the matrix update now as we need the most up to date info we can get here.
        //
        // Then we need to calculate the modelViewMatrix for the object we can project the bounding spheres into clip
        // space and calculate screen area
        this.updateMatrix();
        const frustumCam = camera.getCurrentCamera();
        const frustumMatrix = frustumCam.projectionMatrix.clone().multiply(frustumCam.matrixWorldInverse);
        const camFrustum = new Frustum().setFromProjectionMatrix(frustumMatrix);
        // Need the position of the camera in world space for projecting screen radius of bounding spheres
        const camWorldPos = camera.getWorldPosition(new Vector3());
        const nodeStack = [this.tileset.root];
        while (nodeStack.length > 0) {
            const item = nodeStack.pop();
            // Load the bounding sphere info from the tile
            //
            // We enforce that we have a bounding sphere with resolveBoundingSpheres in the constructor so we can
            // uniformly use the bounding sphere instead of having several code paths for each volume type.
            const boundingSphere = new Sphere();
            boundingSphere.center.x = item.boundingVolume.sphere[0];
            boundingSphere.center.y = item.boundingVolume.sphere[1];
            boundingSphere.center.z = item.boundingVolume.sphere[2];
            boundingSphere.radius = item.boundingVolume.sphere[3];
            // Load the local -> world transform
            const localTransform = item.extras.transform;
            // Transform the bounding sphere into world space
            boundingSphere.applyMatrix4(localTransform);
            boundingSphere.applyMatrix4(this.matrixWorld);
            // Touch the tile's cache node to update the position in the eviction queue
            if (item.extras.content.cacheNode !== undefined) {
                this.tileCache.touch(item.extras.content.cacheNode);
            }
            if (frameData.options.debug.tileFrustumCulling) {
                // Frustum cull tiles outside of the viewport
                if (!camFrustum.intersectsSphere(boundingSphere)) {
                    continue;
                }
            }
            // Get the distance to the tile so we can project the geometric error to screen space error
            const tileToCam = camWorldPos.clone().sub(boundingSphere.center);
            const distanceToTile = tileToCam.length();
            const distanceToTileSurface = Math.max(tileToCam.length() - boundingSphere.radius, 0.0000001);
            const screenSpaceError = MathUtils.projectGeometricError(item.geometricError, screenSize.y, distanceToTileSurface, MathUtils.degreesToRadians(camera.perspective.fov));
            // Fetch the tile's rendering object
            const obj = this.getTileObject(frameData.options.tilesetNetworkRequests.requestSorting ? distanceToTile : 0, item);
            // Update shared attribution credits
            if (obj instanceof GLTFModel) {
                const set = new Set();
                for (const attribution of this.sharedSettings.attributions) {
                    if (attribution.html)
                        set.add(attribution.html);
                }
                for (const credit of obj.credits) {
                    set.add(credit);
                }
                this.sharedSettings.attributions.length = 0;
                for (const credit of set) {
                    this.sharedSettings.attributions.push({
                        html: credit,
                    });
                }
            }
            // Check whether the current tile meets or exceeds our screen space error requirement. This is used to
            // determine when to stop walking the tree as we have met our quality needs.
            const screenSpaceErrorMet = screenSpaceError <= this.sharedSettings.maximumScreenSpaceError;
            // Coerce the item.children array to an empty array if it's undefined as that is semantically what
            // a missing children array means
            const tileChildren = (_a = item.children) !== null && _a !== void 0 ? _a : [];
            // Checks if the tile has no children. This short circuits some of our terminal checks around children.
            const hasNoChildren = tileChildren.length === 0;
            // Checks if all child tiles are fully resident. We have to terminate if they're not all resident.
            const numResidentChildren = tileChildren.filter((v) => {
                const hasNoContent = v.content === undefined || v.content === null;
                // Fetch the field that contains the tile object
                const content = v.extras.content.object;
                let contentLoaded = content !== undefined;
                // A special case to check is if the content is itself another tileset. We need to make sure it actually
                // has content ready to replace the current tile to be considered resident.
                if (content !== undefined) {
                    if (content instanceof Tileset3D) {
                        contentLoaded = content.visibleContentReady(distanceToTile, true);
                    }
                }
                return hasNoContent || contentLoaded;
            }).length;
            const allChildrenResident = numResidentChildren === tileChildren.length;
            // Get whether this tile has any children. If it has no children then we force the tile to be
            // rendered
            const isTerminal = screenSpaceErrorMet || hasNoChildren || !allChildrenResident;
            // If the refinement mode is "ADD" then we have to render this tile if we reach it by walking the
            // graph.
            const mustRenderForRefinement = item.refine === "ADD";
            if (isTerminal || mustRenderForRefinement) {
                if (obj !== undefined && item.content !== undefined && item.content !== null) {
                    if (frameData.options.debug.tileBoundingVolumes) {
                        DebugDraw.addBoundingVolume(frameData.options.debug, item.boundingVolume, localTransform, this.matrixWorld);
                    }
                    // Recurse into nested preparing objects
                    if (obj instanceof PreparingObject)
                        obj.prepare(renderer, camera, frameData);
                    // Add the object to the render list
                    this.add(obj);
                }
            }
            // If not all children are resident then we should make sure we've got network requests in flight to load
            // them. Of course, we should only load them if we need them so we check if we actually want higher detail
            // tiles with 'screenSpaceErrorMet'
            if (!allChildrenResident && !screenSpaceErrorMet) {
                for (const child of tileChildren) {
                    if (child.content !== undefined && child.content !== null) {
                        const _tile = child;
                        this.trySpawnTileLoad(distanceToTile, _tile);
                    }
                    else {
                        child.extras.content.object = new DummyObject();
                    }
                }
            }
            // Add all child nodes to the queue to continue the tree traversal
            if (!isTerminal) {
                nodeStack.push(...tileChildren);
            }
        }
        // Handle a tile eviction cycle after all visible tiles have been touched
        //
        // This should only be triggered by the root tileset. Children should only touch tiles
        if (this.isRoot) {
            this.tileCache.evictNodes(() => {
                return this.sharedSettings.liveTileCost > this.sharedSettings.tileBudget;
            }, (tile) => {
                // Evict the object's data
                const object = tile.extras.content.object;
                if (object !== undefined) {
                    // Decrease the live 'cost' by however much this tile weights itself
                    this.sharedSettings.liveTileCost -= object.tileCost;
                    // Evict the object's data
                    object.evict();
                }
                // Drop all our references to the object to free CPU memory (whenever the GC feels like it anyway)
                tile.extras.content = {};
            });
        }
    }
    applyStyle(styleDesc) {
        this.updateMatrix();
        this.updateMatrixWorld();
        this.sharedSettings.styleDesc = Object.assign({}, styleDesc);
        for (const callback of this.materialRegistry.styleCallbacks) {
            callback(this.styleDesc);
        }
    }
    setClippingPlanes(planes) {
        for (const callback of this.materialRegistry.clippingPlaneCallbacks) {
            callback(planes);
        }
        this.materialRegistry.clippingPlanes = planes;
    }
    invalidateMaterials() {
        for (const callback of this.materialRegistry.invalidateCallbacks) {
            callback();
        }
    }
    /**
     * Walks the tile tree, recursively, to check if there is any resident tile content that can be rendered immediately
     */
    visibleContentReady(distanceToTile, spawnLoadTasks = false) {
        var _a;
        let nodeStack = [this.tileset.root];
        // Perform a breadth-first traversal of the tile graph. We keep looping until we've exhausted all candidates to
        // check. At each depth we can add additional candidates in the next depth. When we run out of candidates to
        // check we have proven that the tile-set has at least *something* to render ready.
        while (nodeStack.length > 0) {
            // Take the nodeStack, replacing with empty list. Breadth first search requires handling all nodes at the
            // same depth at the same time.
            const items = nodeStack;
            nodeStack = [];
            let contradictionFound = false;
            for (const item of items) {
                // Checks whether the tile has any content to load at all.
                const hasContent = item.content !== undefined && item.content !== null;
                // Checks whether there is any tile object loaded and ready for rendering for this tile.
                const tileObject = item.extras.content;
                const contentLoaded = tileObject.object !== undefined;
                // Touch the tile's cache node to update the position in the eviction queue
                if (item.extras.content.cacheNode !== undefined) {
                    this.tileCache.touch(item.extras.content.cacheNode);
                }
                // If the tile has content to render, but it is not resident then we can short circuit and exit. We only
                // need to find one contradiction to prove the tile-set is not ready
                if (hasContent && !contentLoaded) {
                    // Spawn a load task for the missing data if requested
                    if (spawnLoadTasks) {
                        if (item.content !== undefined && item.content !== null) {
                            const _item = item;
                            this.trySpawnTileLoad(distanceToTile, _item);
                        }
                        else {
                            tileObject.object = new DummyObject();
                        }
                    }
                    contradictionFound = true;
                }
                // If the tile doesn't have any content then we need to look for content in the children
                if (!hasContent) {
                    nodeStack.push(...((_a = item.children) !== null && _a !== void 0 ? _a : []));
                }
            }
            if (contradictionFound) {
                return false;
            }
        }
        // If we reach here we have walked as far as needed through the tree without finding a tile with missing data.
        // This means we must be ready to render, so we return true.
        return true;
    }
    get styledTransparent() {
        const opacity = this.styleDesc.opacity;
        return (opacity !== null && opacity !== void 0 ? opacity : 1) < 1;
    }
    evict() {
        // Nothing to do, Tileset3D owns nothing directly and the tileCache drives tile eviction
    }
    getTileObject(distanceToTile, tile) {
        // A tile may not have any content, so we need to check for this
        if (typeof tile.content !== "undefined" && tile.content !== null) {
            const _tile = tile;
            // Get the tile's object metadata, may need to create it if it doesn't exist
            const tileObject = tile.extras.content;
            // Spawn a tile load task if one is not already in flight. It is impossible for tileObject.object to still
            // be undefined once this function returns so it's safe to assert the type below.
            this.trySpawnTileLoad(distanceToTile, _tile);
            return tileObject.object;
        }
        else {
            return undefined;
        }
    }
    trySpawnTileLoad(distanceToTile, tile) {
        const tileObject = tile.extras.content;
        const objectMissing = tileObject.object === undefined;
        const requestMissing = tileObject.request === undefined;
        const requestCancelled = tileObject.request !== undefined && tileObject.request.status === "cancelled";
        // const requestInFlight = tileObject.request !== undefined && tileObject.request.status === "inflight";
        // const requestComplete = tileObject.request !== undefined && tileObject.request.status === "complete";
        const shouldSpawnRequest = objectMissing && (requestMissing || requestCancelled);
        // When .object is undefined it means that no data is available and no streaming request is outstanding for
        // the data. We want the data so create a request for the data and mark .object as null instead of undefined
        // to flag the outstanding request. This way we won't send multiple streaming requests for the same data.
        if (shouldSpawnRequest) {
            // Spawn an async streaming job. Once the job is complete the resulting object will be written into the
            // .object slot to make the loaded tile available.
            const taskFn = () => __awaiter(this, void 0, void 0, function* () {
                const loaded = yield this.loadTile(tile);
                loaded.frustumCulled = false;
                loaded.traverse((v) => (v.frustumCulled = false));
                tileObject.object = loaded;
                // Tileset3D objects are *not* evict-able as they are part of the tile tree
                if (!(loaded instanceof Tileset3D)) {
                    tileObject.cacheNode = this.tileCache.add(tile);
                    this.sharedSettings.liveTileCost += loaded.tileCost;
                }
                return loaded;
            });
            tileObject.request = this.requestManager.queueRequest("tileset", taskFn, distanceToTile);
        }
    }
    loadTile(tile) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const path = (_a = tile.content.uri) !== null && _a !== void 0 ? _a : tile.content.url;
            if (path === undefined || path === null)
                throw new Error("Content missing URI or URL field");
            const url = this.tilesetSource.adjacentResource(path);
            const data = yield FileLoader.loadArrayBuffer(url, {
                headers: this.tilesetSource.bearerToken,
            });
            const view = new DataView(data);
            if (this.owner === undefined)
                throw new Error("Someone forgot to set the owner for a Tileset3D");
            if (PointCloud.isValidHeader(view)) {
                return yield PointCloud.create(tile, new DataView(data), this.sharedSettings.styleDesc, this.materialRegistry, this.pickingManager, this.owner);
            }
            else if (Batch3DModel.isValidHeader(view)) {
                return yield Batch3DModel.create(tile, new DataView(data), this.sharedSettings.styleDesc, this.materialRegistry, this.pickingManager, this.owner);
            }
            else if (Instanced3DModel.isValidHeader(view)) {
                return yield Instanced3DModel.create(tile, new DataView(data), this.sharedSettings.styleDesc, this.materialRegistry, this.pickingManager, this.owner);
            }
            else if (Composite.isValidHeader(view)) {
                return yield Composite.create(tile, new DataView(data), this.sharedSettings.styleDesc, this.materialRegistry, this.pickingManager, this.owner);
            }
            else if (GLTFModel.isValidHeader(view)) {
                return yield GLTFModel.create(tile, new DataView(data), this.sharedSettings.styleDesc, this.materialRegistry, this.pickingManager, this.owner);
            }
            else {
                const parsedTileset = tryParseTileset(data);
                if (parsedTileset !== null) {
                    // Inject the implementation metadata into the tileset. This includes the root Object3D and the
                    // ResourceSource info.
                    const tileset = this.injectTilesetSourceMetadata(parsedTileset, {
                        owner: tile.extras.owner.extras.owner,
                        source: this.tilesetSource.inherit(url),
                    });
                    // Construct the tileset
                    const obj = new Tileset3D({
                        tileset,
                        styleDesc: {},
                        parent: tile,
                        pickingManager: this.pickingManager,
                        requestManager: this.requestManager,
                    });
                    obj.owner = this.owner;
                    return obj;
                }
                else {
                    throw new Error("Unsupported tile format");
                }
            }
        });
    }
    injectTilesetSourceMetadata(tileset, extras) {
        const castedTileset = tileset;
        castedTileset.extras = extras;
        return castedTileset;
    }
    get styleDesc() {
        return this.sharedSettings.styleDesc;
    }
}
function tryParseTileset(data) {
    try {
        const parsed = JSON.parse(new TextDecoder().decode(data));
        if (!isTileset(parsed)) {
            throw new Error("Response does not match tileset interface");
        }
        return parsed;
    }
    catch (e) {
        return null;
    }
}
function resolveContentUrl(sourceUrl, content) {
    const split = sourceUrl.split("?");
    let uri;
    if (content.uri !== undefined && content.uri !== null) {
        uri = content.uri;
    }
    else if (content.url !== undefined && content.url !== null) {
        uri = content.url;
    }
    else {
        throw new Error("Content missing URI or URL field");
    }
    if (split.length === 1) {
        return `${split[0]}/../${uri}`;
    }
    else {
        return `${split[0]}/../${uri}?${split[1]}`;
    }
}
// tslint:disable-next-line:max-classes-per-file
class DummyObject extends Object3D {
    constructor() {
        super(...arguments);
        this.tileCost = 0;
    }
    evict() {
        // Dummy impl
    }
}
